import { Link } from "react-router-dom";
import { Squiggle, Star, SunDoodle } from "../components/Illustrations";
import Reveal from "../components/Reveal";

export default function Story() {
  return (
    <div className="pt-32 pb-24 px-5 md:px-10" data-testid="story-page">
      <div className="mx-auto max-w-[1180px]">
        {/* Eyebrow + Title */}
        <Reveal>
          <p className="eyebrow mb-4 text-center">Our story</p>
        </Reveal>
        <Reveal delay={60}>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[120px] leading-[0.95] tracking-[-0.03em] text-center">
            A mother's <span className="italic">instinct.</span>
          </h1>
        </Reveal>

        {/* Editorial Hero — large portrait + opening paragraph */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-14 mt-16 md:mt-24 items-end">
          <Reveal className="md:col-span-7 relative">
            <div className="aspect-[4/5] rounded-[32px] overflow-hidden bg-[#F3EADA] lift" data-hover="true">
              <img
                src="/founder/founder-1.jpeg"
                alt="Natasha, founder of SOL Kids, holding her daughter — the muse behind the brand"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="story-hero-image"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#8C8C86] font-medium">Plate 01 — The founder &amp; her muse</p>
          </Reveal>

          <Reveal delay={140} className="md:col-span-5 md:pb-10 text-xl md:text-2xl leading-[1.5] text-[#2C2C2A]">
            <p className="font-serif text-3xl md:text-4xl mb-4 leading-[1.15]">It started with a mother's instinct.</p>
            <p>To protect what matters most.</p>
            <p className="mt-5">When I learned how the sun affects little eyes, I knew I wanted better.{" "}
              <span className="font-medium">Safer. Stronger. Smarter.</span>
            </p>
            <Squiggle w={120} className="my-7 opacity-60" />
            <p className="text-[#595955] text-lg md:text-xl">Polarized, to shield those tiny eyes.<br />Unbreakable, because, well, kids.<br />Light as air, so they actually keep them on.</p>
          </Reveal>
        </div>

        {/* Pull quote */}
        <Reveal>
          <div className="mt-24 md:mt-36 text-center max-w-[36ch] mx-auto">
            <Star className="mx-auto wiggle mb-6" size={28} />
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-[-0.025em]">
              Every child wears the world <span className="italic">a little differently.</span>
            </h2>
            <p className="mt-6 text-[#595955] text-lg md:text-xl">Some run. Some lead. Some dream. Some explore. Pick one. Or a few. For all the little versions of them.</p>
          </div>
        </Reveal>

        {/* Editorial spread — second image, intimate moment */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-14 mt-20 md:mt-32 items-center">
          <Reveal delay={100} className="md:col-span-5 md:order-1 text-xl md:text-2xl leading-[1.5] text-[#2C2C2A] md:pr-4">
            <p className="font-hand text-3xl md:text-4xl text-[#DD2E20]/90 mb-5">For the apple of my eye.</p>
            <p>SOL Kids was born in the quiet in-between moments — bedtime stories, weekend picnics, that first beach day where the sun felt too sharp.</p>
            <p className="mt-5 text-[#595955] text-lg md:text-xl">Built for the way they actually move. Engineered for how a parent actually worries.</p>
          </Reveal>
          <Reveal className="md:col-span-7 md:order-2">
            <div className="aspect-[4/5] md:aspect-[5/6] rounded-[32px] overflow-hidden bg-[#F3EADA] lift" data-hover="true">
              <img
                src="/founder/founder-2.jpeg"
                alt="Natasha and her daughter sharing a quiet moment at home"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="story-second-image"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#8C8C86] font-medium">Plate 02 — Quiet afternoons</p>
          </Reveal>
        </div>

        {/* Editorial spread — third image, landscape with caption alongside */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-14 mt-20 md:mt-32 items-center">
          <Reveal className="md:col-span-8">
            <div className="aspect-[4/3] rounded-[32px] overflow-hidden bg-[#F3EADA] lift" data-hover="true">
              <img
                src="/founder/founder-3.jpeg"
                alt="Natasha and her daughter celebrating a birthday — the everyday magic SOL Kids is built for"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="story-third-image"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#8C8C86] font-medium">Plate 03 — Little big moments</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-4 text-xl md:text-2xl leading-[1.5] text-[#2C2C2A]">
            <p>SOL Kids keeps little eyes protected while they{" "}
              <span className="italic">grow, explore, fall,</span> and become who they're meant to be.
            </p>
            <p className="mt-5">Fearless..<br /> …while you worry a little less :)</p>
            <p className="font-hand text-3xl md:text-4xl mt-10">Love &amp; sun-light,<br />Natasha</p>
          </Reveal>
        </div>

        {/* CTA */}
        <Reveal>
          <div className="mt-24 md:mt-32 text-center">
            <SunDoodle size={48} className="mx-auto spin-slow opacity-80 mb-6" />
            <Link to="/collections" className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18] transition-colors" data-testid="story-shop-cta">
              Shop the collection
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
